import {
  DietType,
  getNpcName,
  NPC_DEFINITIONS,
  NpcState,
  type NpcTypeValue,
} from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import { selectedNpc } from '../../state/selection';
import { StatBar } from './StatBar';

// ============================================================================
// Types & Constants
// ============================================================================

const getAIStateName = (state: number): string => {
  const entry = Object.entries(NpcState).find(
    ([key, value]) => value === state && key !== 'EATING' && key !== 'NpcAIState'
  );
  if (!entry) return 'Unknown';
  // Convert SCREAMING_SNAKE_CASE to Title Case (e.g. RETURNING_TO_TERRITORY -> Returning To Territory)
  return entry[0]
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const getDietName = (diet: number): string => {
  const entry = Object.entries(DietType).find(([_, value]) => value === diet);
  if (!entry) return 'Unknown';
  return entry[0]
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

// ============================================================================
// Styles
// ============================================================================

// Custom gold gradient for borders
const GOLD_GRADIENT = 'bg-gradient-to-b from-[#fbf5e9] via-[#bf953f] to-[#b38728]';

const npcInfoPanel = tv({
  slots: {
    root: ['fixed top-4 right-4 z-[1000]', 'w-80', 'animate-slide-left', 'font-sans'],
    // The main container with the metallic border effect
    borderFrame: [
      'p-[2px]', // Width of the gold border
      'rounded-sm',
      GOLD_GRADIENT,
      'shadow-2xl',
    ],
    // The dark inner content background
    contentFrame: [
      'bg-[#1a120b]', // Deep brown/black base
      'bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#2c1f16] to-[#1a120b]',
      'p-1',
      'rounded-sm',
      'relative',
      'overflow-hidden',
    ],
    // Inner decorative line
    innerBorder: ['border border-[#5c4033]/50', 'p-4', 'relative'],

    // Header
    header: 'flex flex-col items-center mb-6 relative z-10',
    titleDecoration: [
      'w-full h-[2px] mb-2',
      'bg-[linear-gradient(90deg,transparent,#bf953f,transparent)]',
      'opacity-80',
    ],
    name: [
      'text-3xl',
      'font-[family-name:var(--font-fantasy)]',
      'text-transparent bg-clip-text',
      'bg-gradient-to-b from-[#fff8e7] to-[#d4af37]',
      'drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]',
      'tracking-widest',
      'uppercase',
      'text-center',
    ],
    dietBadge: [
      'mt-1',
      'text-[10px] uppercase tracking-[0.2em]',
      'text-[#8b7355]',
      'flex items-center gap-2',
      'before:content-[""] before:w-3 before:h-[1px] before:bg-[#8b7355]',
      'after:content-[""] after:w-3 after:h-[1px] after:bg-[#8b7355]',
    ],

    // Content
    section: 'mb-5 relative',
    sectionHeader: [
      'flex items-center gap-3 mb-3',
      'text-xs uppercase tracking-[0.15em]',
      'text-[#bf953f]',
      'font-bold',
      'after:content-[""] after:flex-1 after:h-[1px] after:bg-gradient-to-r after:from-[#bf953f]/50 after:to-transparent',
    ],

    statsGrid: 'grid grid-cols-2 gap-3',
    statBox: [
      'bg-[#0f0a06]/60',
      'border border-[#3d2b1f]',
      'p-2',
      'flex flex-col items-center justify-center',
      'rounded-sm',
    ],
    statLabel: 'text-[9px] uppercase tracking-wider text-[#8b7355] mb-0.5',
    statValue: 'text-[#e8dcc4] font-mono text-sm',

    // Footer
    footer: [
      'mt-2 pt-3',
      'border-t border-[#3d2b1f]',
      'flex justify-between items-center',
      'text-[10px] text-[#5c4a3d] font-mono',
    ],

    // Corner flourishes (SVG style via CSS)
    cornerTL:
      'absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#bf953f] rounded-tl-sm opacity-60',
    cornerTR:
      'absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#bf953f] rounded-tr-sm opacity-60',
    cornerBL:
      'absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#bf953f] rounded-bl-sm opacity-60',
    cornerBR:
      'absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#bf953f] rounded-br-sm opacity-60',
  },
});

// Rich state badge styling
const stateBadge = tv({
  base: [
    'absolute top-2 right-2',
    'px-2 py-0.5',
    'text-[9px] uppercase tracking-wider font-bold',
    'border border-white/10',
    'shadow-[0_0_10px_rgba(0,0,0,0.5)]',
    'backdrop-blur-sm',
    'rounded-sm',
  ],
  variants: {
    state: {
      idle: 'bg-emerald-950/40 text-emerald-400 border-emerald-500/30',
      wandering: 'bg-cyan-950/40 text-cyan-400 border-cyan-500/30',
      grazing: 'bg-lime-950/40 text-lime-400 border-lime-500/30',
      hunting: 'bg-red-950/60 text-red-400 border-red-500/50 shadow-red-900/20',
      stalking: 'bg-rose-950/40 text-rose-400 border-rose-500/30',
      fleeing: 'bg-yellow-950/40 text-yellow-400 border-yellow-500/30',
      fighting: 'bg-red-900/60 text-red-200 border-red-500/50',
      sleeping: 'bg-indigo-950/40 text-indigo-300 border-indigo-500/30',
      resting: 'bg-blue-950/40 text-blue-300 border-blue-500/30',
      drinking: 'bg-sky-950/40 text-sky-300 border-sky-500/30',
      socializing: 'bg-pink-950/40 text-pink-300 border-pink-500/30',
      patrolling: 'bg-teal-950/40 text-teal-300 border-teal-500/30',
      defending: 'bg-orange-950/40 text-orange-400 border-orange-500/30',
      mating: 'bg-fuchsia-950/40 text-fuchsia-300 border-fuchsia-500/30',
      caring: 'bg-pink-950/40 text-pink-300 border-pink-500/30',
      migrating: 'bg-violet-950/40 text-violet-300 border-violet-500/30',
      hiding: 'bg-stone-950/40 text-stone-300 border-stone-500/30',
      dead: 'bg-slate-950/60 text-slate-500 border-slate-600/30',
      injured: 'bg-red-950/40 text-red-300 border-red-800/30',
      alert: 'bg-amber-950/40 text-amber-300 border-amber-500/30',
      investigating: 'bg-purple-950/40 text-purple-300 border-purple-500/30',
      following: 'bg-purple-950/40 text-purple-300 border-purple-500/30',
      returning: 'bg-blue-950/40 text-blue-300 border-blue-500/30',
      unknown: 'bg-slate-800 text-slate-400',
    },
  },
  defaultVariants: {
    state: 'unknown',
  },
});

type NpcStateKey = keyof typeof stateBadge.variants.state;

// ============================================================================
// Component
// ============================================================================

export const NpcInfoPanel = () => {
  const npc = selectedNpc.value;

  if (!npc) {
    return null;
  }

  const typeName = getNpcName({ npcType: npc.npcType });
  const stateText = getAIStateName(npc.aiState);

  // Try to map current state to a variant key, defaulting to 'unknown'
  let stateKey: NpcStateKey = 'unknown';
  const rawStateName = stateText.toLowerCase().split(' ')[0] ?? ''; // Take first word

  // Check if the state exists in our variants
  const variants = stateBadge.variants.state;
  if (rawStateName in variants) {
    stateKey = rawStateName as NpcStateKey;
  }

  const styles = npcInfoPanel();

  // Get animal definition for diet info
  const definition = NPC_DEFINITIONS.get(npc.npcType as NpcTypeValue);
  const dietName = definition ? getDietName(definition.diet) : 'Unknown';

  return (
    <div class={styles.root()}>
      {/* Outer Metallic Frame */}
      <div class={styles.borderFrame()}>
        {/* Inner Content Background */}
        <div class={styles.contentFrame()}>
          {/* Inner Decorative Border */}
          <div class={styles.innerBorder()}>
            {/* Corner Flourishes */}
            <div class={styles.cornerTL()} />
            <div class={styles.cornerTR()} />
            <div class={styles.cornerBL()} />
            <div class={styles.cornerBR()} />

            {/* Floating State Badge */}
            <div class={stateBadge({ state: stateKey })}>{stateText}</div>

            {/* Header Area */}
            <div class={styles.header()}>
              <div class={styles.titleDecoration()} />
              <h2 class={styles.name()}>{typeName}</h2>
              <div class={styles.dietBadge()}>{dietName}</div>
            </div>

            {/* Vitality Section */}
            <div class={styles.section()}>
              <div class={styles.sectionHeader()}>Vitality</div>
              <StatBar
                label="Health Points"
                value={npc.health}
                max={npc.maxHealth}
                color="health"
              />
            </div>

            {/* Needs Section */}
            <div class={styles.section()}>
              <div class={styles.sectionHeader()}>Current Needs</div>
              <div class="flex flex-col gap-2.5">
                <StatBar label="Hunger" value={Math.round((1 - npc.hunger) * 100)} color="hunger" />
                <StatBar
                  label="Energy"
                  value={Math.round((1 - npc.tiredness) * 100)}
                  color="rest"
                />
                {npc.fear > 0 && (
                  <StatBar label="Fear" value={Math.round(npc.fear * 100)} color="mood" />
                )}
              </div>
            </div>

            {/* Attributes Grid */}
            {definition && (
              <div class={styles.section()}>
                <div class={styles.sectionHeader()}>Attributes</div>
                <div class={styles.statsGrid()}>
                  <div class={styles.statBox()}>
                    <span class={styles.statLabel()}>Combat</span>
                    <span class={styles.statValue()}>{definition.combatPower}</span>
                  </div>
                  <div class={styles.statBox()}>
                    <span class={styles.statLabel()}>Armor</span>
                    <span class={styles.statValue()}>{definition.armorValue}</span>
                  </div>
                  <div class={styles.statBox()}>
                    <span class={styles.statLabel()}>Speed</span>
                    <span class={styles.statValue()}>{definition.moveSpeed}</span>
                  </div>
                  <div class={styles.statBox()}>
                    <span class={styles.statLabel()}>Radius</span>
                    <span class={styles.statValue()}>{definition.territoryRadius}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Footer Information */}
            <div class={styles.footer()}>
              <span>
                POS: {Math.floor(npc.x)}, {Math.floor(npc.y)}
              </span>
              {npc.packId > 0 && (
                <span class="text-[#bf953f] flex items-center gap-1">
                  PACK #{npc.packId} {npc.isLeader && '★'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
